import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Stack, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import { Play as PlayIcon } from 'lucide-react';
import { useRoutineStore } from '../store/routineStore';
import { useExerciseStore } from '../store/exerciseStore';

const WeeklyPlan = ({ onStartWorkout }) => {
  const routines = useRoutineStore((state) => state.routines);
  const exercises = useExerciseStore((state) => state.exercises);
  const [selectedDay, setSelectedDay] = useState(null);
  const [expandedWorkout, setExpandedWorkout] = useState(null);

  const daysLabel = {
    domingo: 'Dom',
    lunes: 'Lun',
    martes: 'Mar',
    miércoles: 'Mié',
    jueves: 'Jue',
    viernes: 'Vie',
    sábado: 'Sáb',
  };

  const daysOrder = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const today = daysOrder[new Date().getDay()];

  const weeklySchedule = daysOrder.reduce((acc, day) => {
    const workouts = [];
    routines.filter((r) => r.active).forEach((routine) => {
      routine.days.forEach((d) => {
        if (d.weekDays && d.weekDays.includes(day)) {
          workouts.push({ routineId: routine.id, routineName: routine.name, day: d });
        }
      });
    });
    acc[day] = { day, isToday: day === today, workouts };
    return acc;
  }, {});

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          📅 Planificación Semanal
        </Typography>

        <Grid container spacing={1}>
          {daysOrder.map((day) => {
            const dayData = weeklySchedule[day];
            const hasWorkout = dayData.workouts && dayData.workouts.length > 0;

            return (
              <Grid item xs={6} sm={4} md={1.7} key={day}>
                <Box
                  onClick={() => setSelectedDay(dayData)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    textAlign: 'center',
                    border: '2px solid',
                    borderColor: dayData.isToday ? 'primary.main' : 'divider',
                    bgcolor: dayData.isToday ? 'primary.light' : hasWorkout ? 'success.light' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 2,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: dayData.isToday ? 'bold' : 'normal',
                      color: dayData.isToday ? 'primary.main' : 'text.secondary',
                      fontSize: '0.75rem',
                    }}
                  >
                    {daysLabel[day]}
                  </Typography>

                  {dayData.isToday && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                        mx: 'auto',
                        my: 0.5,
                      }}
                    />
                  )}

                  <Stack spacing={0.5} sx={{ minHeight: 40 }}>
                    {hasWorkout ? (
                      dayData.workouts.map((workout, idx) => (
                        <Chip
                          key={idx}
                          label={workout.routineName.split(':')[0]}
                          size="small"
                          variant="filled"
                          color={dayData.isToday ? 'primary' : 'success'}
                          sx={{
                            fontSize: '0.65rem',
                            height: 'auto',
                            '& .MuiChip-label': {
                              px: 0.5,
                              py: 0.25,
                              whiteSpace: 'normal',
                              lineHeight: 1.2,
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontSize: '0.7rem',
                          py: 1,
                        }}
                      >
                        Descanso
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main' }} />
            <Typography variant="caption">Hoy</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: 'success.main' }} />
            <Typography variant="caption">Con Rutina</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>

    {/* Day detail dialog */}
    <Dialog
      open={!!selectedDay}
      onClose={() => { setSelectedDay(null); setExpandedWorkout(null); }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ textTransform: 'capitalize' }}>
        {selectedDay?.day}
        {selectedDay?.isToday && (
          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main', fontWeight: 'bold' }}>
            · Hoy
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {selectedDay?.workouts?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Día de descanso 💤
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {selectedDay?.workouts?.map((workout, idx) => {
              const isExpanded = expandedWorkout === idx;
              return (
              <Box
                key={idx}
                sx={{
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isExpanded ? 'primary.main' : 'divider',
                  transition: 'border-color 0.2s',
                }}
              >
                {/* Header — always visible, click to expand */}
                <Box
                  onClick={() => setExpandedWorkout(isExpanded ? null : idx)}
                  sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {workout.day.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {workout.day.exercises?.length || 0} ejercicios · {workout.routineName}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {isExpanded ? '▲' : '▼'}
                  </Typography>
                </Box>

                {/* Exercise list — only when expanded */}
                {isExpanded && (
                  <>
                    <Stack spacing={0.5} sx={{ mt: 1.5, mb: 1.5 }}>
                      {workout.day.exercises?.map((ex, i) => {
                        const exData = exercises.find((e) => e.id === ex.exerciseId);
                        const name = ex.name || exData?.name || `Ejercicio ${i + 1}`;
                        return (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              px: 1,
                              py: 0.5,
                              bgcolor: 'background.paper',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              {name}
                            </Typography>
                            <Chip
                              label={`${ex.sets} × ${ex.reps}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Box>
                        );
                      })}
                    </Stack>

                    
                  </>
                )}
                {onStartWorkout && (
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        startIcon={<PlayIcon size={16} />}
                        onClick={() => {
                          onStartWorkout(workout.routineId, workout.day.id);
                          setSelectedDay(null);
                          setExpandedWorkout(null);
                        }}
                      >
                        Empezar
                      </Button>
                    )}
              </Box>
            );})}

          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setSelectedDay(null); setExpandedWorkout(null); }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  </>);
};

export default WeeklyPlan;
